<template>
    <div class="videos-container px-12 lg:px-32 py-32">
      <div class="flex justify-between items-center mb-8">
        <h1 class="text-4xl font-bold">{{ $t('videos.title') }}</h1>
        
        <!-- Cache controls (only show in development) -->
        <div v-if="isDevelopment" class="flex items-center space-x-4 text-sm">
          <button 
            @click="refreshVideos"
            class="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs transition-colors"
            :disabled="isLoading"
          >
            🔄 Refresh
          </button>
          <button 
            @click="clearVideoCache"
            ref="clearCacheButton"
            class="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-xs transition-colors"
          >
            🗑️ Clear Cache
          </button>
          <span class="text-gray-500 text-xs">
            Cache: {{ getCacheInfo().totalSize }}
          </span>
        </div>
      </div>
      
      <!-- Loading state -->
      <div v-if="isLoading" class="flex justify-center items-center py-16">
        <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600"></div>
        <span class="ml-4 text-gray-600">{{ $t('videos.loading') }}</span>
      </div>
      
      <!-- Error state -->
      <div v-else-if="error" class="text-center py-16">
        <div class="text-red-600 mb-4">
          <i class="fas fa-exclamation-triangle text-4xl"></i>
        </div>
        <h3 class="text-xl font-semibold mb-2 text-gray-900">{{ $t('videos.error.title') }}</h3>
        <p class="text-gray-600 mb-6">{{ error }}</p>
        
        <div class="flex flex-col gap-4 justify-center items-center">
          <!-- YouTube Channel Button (Primary) -->
          <a 
            href="https://www.youtube.com/@facecageco" 
            target="_blank"
            rel="noopener noreferrer"
            @click="trackYouTubeChannelClick"
            class="bg-black hover:bg-gray-800 text-white py-3 px-6 rounded-lg transition-colors duration-300 flex items-center space-x-2 text-lg font-semibold"
            style="color: white !important;"
          >
            <i class="fab fa-youtube text-2xl text-white"></i>
            <span class="text-white">{{ $t('videos.error.visitChannel') }}</span>
            <i class="fas fa-external-link-alt text-sm text-white"></i>
          </a>
          
          <!-- Try Again Button (Secondary) -->
          <button 
            @click="fetchVideos" 
            class="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors duration-300 text-sm"
          >
            {{ $t('videos.error.tryAgain') }}
          </button>
        </div>
      </div>
      
      <!-- Videos grid -->
      <div v-else-if="videosList.length > 0" class="videos-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <video-card 
          v-for="video in videosList" 
          :key="video.id" 
          :video="video"
        />
      </div>
      
      <!-- No videos state -->
      <div v-else class="text-center py-16">
        <div class="text-gray-400 mb-4">
          <i class="fab fa-youtube text-6xl"></i>
        </div>
        <h3 class="text-xl font-semibold mb-2 text-gray-900">{{ $t('videos.empty.title') }}</h3>
        <p class="text-gray-600">{{ $t('videos.empty.message') }}</p>
      </div>
    </div>
  </template>
  
<script>
import axios from 'axios';
import VideoCard from '@/components/VideoCard.vue';
import { useAutoTracking } from '@/utils/auto-tracking';
import { trackAPICall, trackError } from '@/utils/gtm-tracking';
import { videoCache } from '@/utils/video-cache';
import { youtubeStatus } from '@/utils/youtube-status';

export default {
  components: {
    VideoCard
  },
  setup() {
    // Auto-track page interactions
    useAutoTracking();
    
    return {};
  },
  data() {
    return {
      videosList: [],
      isLoading: true,
      errorKey: null, // Store error key instead of translated message
      channelHandle: '@facecageco', // YouTube channel handle
      channelId: null, // Will be resolved from handle
      maxResults: 12 // Number of videos to fetch
    }
  },
  computed: {
    isDevelopment() {
      // Use Vue's built-in development detection
      return process.env.NODE_ENV === 'development' && process.env.VUE_APP_SHOW_DEBUG === 'true';
    },
    error() {
      // Reactive computed property for error message
      return this.errorKey ? this.$t(this.errorKey) : null;
    }
  },
  mounted() {
    this.fetchVideos();
  },
  methods: {
    async fetchVideos() {
      this.isLoading = true;
      this.errorKey = null;
      
      // Set loading status in store
      youtubeStatus.setLoading(true);
      
      console.log('🔍 Starting to fetch videos...');
      console.log('📝 API Key exists:', !!process.env.VUE_APP_YOUTUBE_API_KEY);
      console.log('🔑 API Key preview:', process.env.VUE_APP_YOUTUBE_API_KEY ? process.env.VUE_APP_YOUTUBE_API_KEY.substring(0, 10) + '...' : 'NOT FOUND');
      
      // Check if API key is available
      if (!process.env.VUE_APP_YOUTUBE_API_KEY) {
        console.error('❌ YouTube API key not found!');
        this.errorKey = 'videos.error.apiKeyMissing';
        this.isLoading = false;
        youtubeStatus.setError('api_key_missing');
        return;
      }
      
      let channelId = null;
      
      try {
        // First, try to get the channel ID
        channelId = await this.getChannelId();
        
        // Check for cached videos first
        const cachedVideos = videoCache.getCachedVideoData(channelId);
        if (cachedVideos && cachedVideos.length > 0) {
          console.log('📦 Using cached videos:', cachedVideos.length, 'videos');
          this.videosList = cachedVideos;
          this.isLoading = false;
          youtubeStatus.setSuccess(); // Videos loaded successfully from cache
          return;
        }
        
        console.log('🌐 No cache found, fetching from YouTube API...');
        
        // Track videos fetch start
        const videosTrackingId = trackAPICall('youtube_videos_fetch', 'start');

        console.log('🎬 Fetching videos for channel:', channelId);

        // Fetch videos from the channel
        const videosResponse = await axios.get(
          'https://www.googleapis.com/youtube/v3/search',
          {
            params: {
              part: 'snippet',
              channelId: channelId,
              type: 'video',
              order: 'date',
              maxResults: 12,
              key: process.env.VUE_APP_YOUTUBE_API_KEY
            }
          }
        );

        console.log('🎥 Videos response:', videosResponse.data);
        console.log('📊 Videos found:', videosResponse.data.items.length);

        // Process and cache videos (including thumbnails)
        const processedVideos = await videoCache.processAndCacheVideos(videosResponse.data.items);
        
        // Cache the video data
        videoCache.cacheVideoData(channelId, processedVideos);
        
        this.videosList = processedVideos;
        
        // Track successful videos fetch
        trackAPICall('youtube_videos_fetch', 'success', videosTrackingId, {
          videos_count: this.videosList.length,
          channel_id: channelId,
          cache_used: false
        });
        
        // Set success status in store
        youtubeStatus.setSuccess();
        
        console.log('✅ Videos loaded and cached successfully!', this.videosList.length, 'videos');
        
      } catch (error) {
        console.error('❌ Error fetching videos:', error);
        console.error('❌ Error response:', error.response?.data);
        console.error('❌ Error status:', error.response?.status);
        console.error('❌ Error message:', error.message);
        
        let errorKey = 'videos.error.connectionError';
        let errorType = 'network';
        
        if (error.response?.status === 403) {
          errorKey = 'videos.error.quotaExceeded';
          errorType = 'quota';
        } else if (error.response?.status === 400) {
          errorKey = 'videos.error.invalidRequest';
          errorType = 'api';
        } else if (error.message.includes('Channel not found')) {
          errorKey = 'videos.error.channelNotFound';
          errorType = 'channel';
        }
        
        // Try to load cached data as fallback even if expired
        if (channelId) {
          try {
            const fallbackCache = localStorage.getItem(`fcco_videos_${channelId}`);
            if (fallbackCache) {
              const parsed = JSON.parse(fallbackCache);
              console.log('📦 Using expired cache as fallback');
              this.videosList = parsed.videos || [];
              errorKey = 'videos.error.cacheUsed';
            }
          } catch (cacheError) {
            console.warn('⚠️ Failed to load fallback cache:', cacheError);
          }
        }
        
        this.errorKey = errorKey;
        
        // Set error status in store
        youtubeStatus.setError(errorType);
        
        // Track error
        trackError(error.message, 'youtube_api_error', {
          endpoint: 'youtube_videos',
          error_type: error.response?.status || 'unknown',
          error_details: error.response?.data?.error?.message || error.message
        });
        
      } finally {
        this.isLoading = false;
        console.log('🏁 Finished loading videos. isLoading:', this.isLoading);
        
        // Log cache statistics
        const cacheStats = videoCache.getCacheStats();
        console.log('📊 Cache stats:', cacheStats);
      }
    },
    async getChannelId() {
      // Track API call start
      const trackingId = trackAPICall('youtube_channel_resolve', 'start');
      
      console.log('🔍 Searching for channel: facecageco');
      
      // Method 1: Try to search for channel
      const channelResponse = await axios.get(
        'https://www.googleapis.com/youtube/v3/search',
        {
          params: {
            part: 'snippet',
            type: 'channel',
            q: 'facecageco',
            key: process.env.VUE_APP_YOUTUBE_API_KEY,
            maxResults: 1
          }
        }
      );

      console.log('📺 Channel search response:', channelResponse.data);

      if (channelResponse.data.items.length === 0) {
        console.warn('⚠️ Channel not found via search, trying direct approach...');
        
        // Method 2: Try using channel handle directly (newer YouTube API feature)
        try {
          const channelByHandle = await axios.get(
            'https://www.googleapis.com/youtube/v3/channels',
            {
              params: {
                part: 'snippet,contentDetails',
                forHandle: 'facecageco',
                key: process.env.VUE_APP_YOUTUBE_API_KEY
              }
            }
          );
          
          if (channelByHandle.data.items.length === 0) {
            throw new Error('Channel not found with handle either');
          }
          
          const channelId = channelByHandle.data.items[0].id;
          console.log('✅ Found channel ID via handle:', channelId);
          trackAPICall('youtube_channel_resolve', 'success', trackingId);
          return channelId;
        } catch (handleError) {
          console.error('❌ Channel handle lookup failed:', handleError);
          throw new Error('Channel "facecageco" not found. Please check the channel name.');
        }
      }

      const channelId = channelResponse.data.items[0].snippet.channelId || channelResponse.data.items[0].id;
      console.log('✅ Found channel ID:', channelId);
      
      // Track successful API call
      trackAPICall('youtube_channel_resolve', 'success', trackingId);
      
      return channelId;
    },
    getYoutubeThumbnail(youtubeId, thumbnails = null) {
      // Use the best available thumbnail from API response, or fallback to standard URL
      if (thumbnails) {
        if (thumbnails.maxres) return thumbnails.maxres.url;
        if (thumbnails.high) return thumbnails.high.url;
        if (thumbnails.medium) return thumbnails.medium.url;
      }
      
      // Fallback to YouTube's standard thumbnail URL
      return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
    },
    loadFallbackVideos() {
      // Fallback to static videos from translation files if API fails
      try {
        const videosData = this.$i18n.t('videos');
        const videos = [];
        
        Object.keys(videosData).forEach(key => {
          if (key.startsWith('video') && typeof videosData[key] === 'object') {
            const video = videosData[key];
            const id = key.replace('video', '');
            
            videos.push({
              id: id,
              title: video.title,
              description: video.description,
              youtubeId: video.youtubeId,
              publishedAt: video.publishedAt,
              thumbnailUrl: this.getYoutubeThumbnail(video.youtubeId)
            });
          }
        });
        
        this.videosList = videos;
      } catch (fallbackError) {
        console.error('Error loading fallback videos:', fallbackError);
      }
    },
    
    // Cache management methods
    clearVideoCache() {
      videoCache.clearAllCaches();
      console.log('🗑️ Video cache cleared');
      
      // Show user feedback
      if (this.$refs.clearCacheButton) {
        this.$refs.clearCacheButton.textContent = 'Cache Cleared!';
        setTimeout(() => {
          if (this.$refs.clearCacheButton) {
            this.$refs.clearCacheButton.textContent = 'Clear Cache';
          }
        }, 2000);
      }
    },
    
    async refreshVideos() {
      // Clear cache and fetch fresh data
      const channelId = await this.getChannelId().catch(() => null);
      if (channelId) {
        videoCache.clearVideoCache(channelId);
      }
      
      await this.fetchVideos();
    },
    
    getCacheInfo() {
      return videoCache.getCacheStats();
    },
    
    trackYouTubeChannelClick() {
      // Track when user clicks the YouTube channel button from error state
      trackAPICall('youtube_channel_visit', 'click', null, {
        source: 'error_fallback',
        channel: 'facecageco'
      });
    }
  }
}
</script>
  
<style scoped>
.videos-container {
  min-height: 100vh;
}
</style>
